import { businessMs, formatBusinessDuration, addBusinessMs, formatDateTime } from './utils';
import { SECTION_WEIGHTS } from '@/lib/assemblySections';
import { pausedBusinessMs, type Interval } from '@/lib/pauses';

/**
 * Minimum acceptable duration (ms) for each stage.
 * Used when deciding which stage absorbs the lunch break deduction:
 * the target must still meet its minimum after the deduction is applied.
 * These are NOT hard display floors — estimated stages may show values
 * below these limits when there is genuinely no business time available.
 */
export const STAGE_MIN_DURATION_MS: Record<string, number> = {
  assembly:     30 * 60_000, // 30 min
  software:     30 * 60_000, // 30 min
  benchmarking: 60 * 60_000, // 1 hr
  finishing:     5 * 60_000, // 5 min
  final_touches: 5 * 60_000, // 5 min
};

/** No build fits in less than the sum of the stage minimums (2h 10m). A
 *  recorded span shorter than that was ticked after the work, not during it. */
export const MIN_PLAUSIBLE_BUILD_MS = Object.values(STAGE_MIN_DURATION_MS).reduce((a, b) => a + b, 0);

/** How far a build's duration can be trusted.
 *  - `tracked`: the recorded start and end are plausible; shown as-is.
 *  - `from_stress_test`: the stress test ran before the first recorded event,
 *    so the recorded start is provably late. Measured from the stress test
 *    instead, which is a lower bound (assembly and software came before it).
 *  - `untracked`: the recorded span is too short to be a build and there is
 *    no earlier evidence. No duration is claimed. */
export type DurationKind = 'tracked' | 'from_stress_test' | 'untracked';

export interface BuildDuration {
  kind: DurationKind;
  /** What to display: "4h 10m", "≥ 6h 2m", or null when untracked. The
   *  assembly duration: paused time (other than rejected pauses) is removed. */
  label: string | null;
  /** The figure behind `label`, in business ms (lunch included, as on the
   *  timeline). The lower bound for `from_stress_test`; null when untracked. */
  ms: number | null;
  /** The order duration (lead time): the same span with pauses left in.
   *  Equal to `label` / `ms` when nothing was paused. */
  grossLabel: string | null;
  grossMs: number | null;
  /** Business ms of paused time inside the measured span; 0 when none. */
  pausedMs: number;
  /** The span the timestamps alone give. Kept for `untracked`, so the
   *  reviewer can see what was recorded. */
  recordedLabel: string | null;
  /** One sentence explaining a non-`tracked` result. */
  reason: string | null;
}

/**
 * Judge whether a build's recorded start→end span is a real duration.
 *
 * The checklist only records when a technician ticks it, and some tick the
 * whole checklist after the work is done (ASO-0000103-1: built and stress
 * tested on 28 Oct, every stage ticked within 4 seconds on 29 Oct, shown as
 * 5m). The stress test's start comes from the HWiNFO log, written by the
 * machine during the run, so it cannot be backfilled. When it is earlier
 * than everything recorded, it is the better start.
 */
export function assessBuildDuration(
  start: string | null | undefined,
  end: string | null | undefined,
  stressStartedAt: string | null | undefined,
  activeDays?: Set<string>,
  /** Merged pause intervals for what is being measured (a unit, or the
   *  order), already without rejected pauses. See lib/pauses. */
  pauses?: ReadonlyArray<Interval>,
): BuildDuration {
  if (!start || !end) {
    return { kind: 'tracked', label: null, ms: null, grossLabel: null, grossMs: null, pausedMs: 0, recordedLabel: null, reason: null };
  }
  const recordedGrossMs = businessMs(new Date(start), new Date(end), activeDays, true);
  const recordedPausedMs = pausedBusinessMs(start, end, pauses, activeDays);
  const recordedMs = recordedGrossMs - recordedPausedMs;
  const recordedLabel = formatDurationMs(recordedMs);

  // A minute of slack so a log that starts in the same minute as the
  // recorded start doesn't count as earlier.
  if (stressStartedAt && new Date(stressStartedAt).getTime() < new Date(start).getTime() - 60_000) {
    const grossMs = businessMs(new Date(stressStartedAt), new Date(end), activeDays, true);
    const pausedMs = pausedBusinessMs(stressStartedAt, end, pauses, activeDays);
    const ms = grossMs - pausedMs;
    return {
      kind: 'from_stress_test',
      label: `≥ ${formatDurationMs(ms)}`,
      ms,
      grossLabel: `≥ ${formatDurationMs(grossMs)}`,
      grossMs,
      pausedMs,
      recordedLabel,
      reason: `Checklist was ticked after the work. Measured from the stress test (${formatDateTime(stressStartedAt)}), so the build took at least this long.`,
    };
  }

  // Judged on the time worked, not the time elapsed: a long span that was
  // nearly all paused is still a checklist ticked after the work.
  if (recordedMs < MIN_PLAUSIBLE_BUILD_MS) {
    return {
      kind: 'untracked',
      label: null,
      ms: null,
      grossLabel: null,
      grossMs: null,
      pausedMs: recordedPausedMs,
      recordedLabel,
      reason: recordedPausedMs > 0
        ? `Checklist was ticked after the work: only ${recordedLabel} was recorded outside pauses, and no stress test predates it.`
        : `Checklist was ticked after the work: only ${recordedLabel} was recorded, and no stress test predates it.`,
    };
  }

  return {
    kind: 'tracked',
    label: recordedLabel,
    ms: recordedMs,
    grossLabel: formatDurationMs(recordedGrossMs),
    grossMs: recordedGrossMs,
    pausedMs: recordedPausedMs,
    recordedLabel,
    reason: null,
  };
}

/** "Xh Ym", matching formatBusinessDuration. */
export function formatDurationMs(ms: number): string {
  const hours = Math.floor(ms / 3_600_000);
  const mins = Math.round((ms % 3_600_000) / 60_000);
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
}

/**
 * One order's build duration, measured the way the order's detail page
 * measures it: the stage timeline's first start to its last completion, ended
 * at the technician's signature rather than a later completed_at, then judged
 * by assessBuildDuration.
 */
export function measureOrderBuild(
  order: {
    started_at?: string | null;
    tech_signed_at?: string | null;
    completed_at?: string | null;
    completed_date?: string | null;
    stage_timestamps?: Record<string, any> | null;
  },
  stressStartedAt: string | null | undefined,
  activeDays?: Set<string>,
  /** The order's pause intervals (see orderPauseIntervals). */
  pauses?: ReadonlyArray<Interval>,
): BuildDuration {
  const ts = order.stage_timestamps || {};
  const stages = buildStageTimeline(ts, {
    orderStart: order.started_at || null,
    orderEnd: resolveTimelineOrderEnd(order, ts),
    activeDays,
    pauses,
  });
  const first = stages[0]?.started;
  const last = [...stages].reverse().find(r => r.completed)?.completed;
  return assessBuildDuration(first, last, stressStartedAt, activeDays, pauses);
}

export interface BuildTimeSummary {
  /** Median of the counted builds, in business ms; null when none count. */
  medianMs: number | null;
  /** Builds with a usable figure: tracked, or a stress-test lower bound. */
  counted: number;
  /** Every build considered, counted or not. */
  total: number;
}

/**
 * Typical build time across many orders.
 *
 * The median, not the mean: an order that waits weeks for parts is
 * indistinguishable from work in the data, and one such order outweighs
 * dozens of normal builds in a mean (one all-time order counted 471h and
 * pulled the mean to 33h against a median of 12h). Untracked builds are left
 * out rather than counted as minutes; stress-test lower bounds are kept,
 * since they are real evidence and only ever err short.
 */
export function summariseBuildTimes(durations: ReadonlyArray<BuildDuration>): BuildTimeSummary {
  const values = durations
    .filter(d => d.kind !== 'untracked' && d.ms != null)
    .map(d => d.ms!)
    .sort((a, b) => a - b);
  const n = values.length;
  const medianMs = n === 0 ? null : n % 2 ? values[(n - 1) / 2] : (values[n / 2 - 1] + values[n / 2]) / 2;
  return { medianMs, counted: n, total: durations.length };
}

/** Earliest stress test start across the given rows, or null. */
export function earliestStressStart(
  tests: ReadonlyArray<{ started_at: string | null }> | null | undefined,
): string | null {
  const starts = (tests ?? []).map(t => t.started_at).filter((s): s is string => !!s);
  if (!starts.length) return null;
  return starts.reduce((a, b) => (new Date(a) < new Date(b) ? a : b));
}

/** Stage row produced by {@link buildStageTimeline}. */
export type StageRow = {
  stage: string;
  key: string;
  started: string | null;
  completed: string | null;
  duration: string;
  /** True when one or both endpoints were synthesised from weights, not real data. */
  estimated?: boolean;
  /** Business ms of paused time inside this stage, already left out of `duration`. */
  pausedMs?: number;
};

/** Sections shown in the timeline, in order. */
export const STAGE_TIMELINE_SECTIONS: ReadonlyArray<readonly [string, string]> = [
  ['assembly', 'Assembly'],
  ['software', 'Software'],
  ['benchmarking', 'Benchmarking'],
  ['finishing', 'Finishing'],
  ['final_touches', 'Final Touches'],
];

/**
 * Resolve the best "tech finished" timestamp for use as the timeline's
 * upper-bound anchor.
 *
 * The authoritative "tech finished" moment is when the technician clicks
 * Submit in the sign-off dialog (which advances the order to
 * `awaiting_review` and sets `completed_at`). `approveOrder` in
 * Production / AssemblyOrders no longer overwrites `completed_at` going
 * forward — the admin-posting moment lives in `posted_date` instead.
 *
 * However, orders posted BEFORE that write-side fix have `completed_at`
 * overwritten with the admin-posting time, which can be hours or days
 * after the tech actually finished. For those, `tech_signed_at` (captured
 * when the signature was saved — moments before Submit) is a much more
 * reliable proxy, and it is also perfectly accurate for new orders.
 *
 * Preference order (always prefer the earliest accurate anchor):
 *  1. `tech_signed_at`              — always accurate when present
 *  2. `completed_at`                — correct for new orders with no
 *                                     signature; may be the admin-posting
 *                                     time on legacy orders (best effort)
 *  3. latest `*_completed` in `ts`  — last explicit checklist tick
 *  4. `completed_date`              — final legacy fallback
 */
export function resolveTimelineOrderEnd(
  order: {
    tech_signed_at?: string | null;
    completed_at?: string | null;
    completed_date?: string | null;
  } | null | undefined,
  ts: Record<string, any> | null | undefined,
): string | null {
  if (!order) return null;
  if (order.tech_signed_at) return order.tech_signed_at;
  if (order.completed_at) return order.completed_at;

  if (ts) {
    const latestCompleted = Object.entries(ts)
      .filter(([k, v]) => k !== 'units' && typeof v === 'string' && k.endsWith('_completed'))
      .map(([, v]) => v as string)
      .sort()
      .pop();
    if (latestCompleted) return latestCompleted;
  }

  return order.completed_date || null;
}

/** Round an ISO timestamp to the nearest whole minute. The display layer
 *  drops seconds via formatDateTime, so this keeps the duration math
 *  consistent with what the user actually sees in the Started/Completed
 *  columns. Without it, a value like 16:30:31 would display as "16:30" but
 *  contribute only 29 business minutes to 17:00, not 30. */
export function snapIsoToMinute(iso: string): string {
  const d = new Date(iso);
  const secs = d.getSeconds() + d.getMilliseconds() / 1000;
  if (secs >= 30) d.setMinutes(d.getMinutes() + 1);
  d.setSeconds(0, 0);
  return d.toISOString();
}

export interface BuildStageTimelineOptions {
  /** When the order moved to "building" (the "Start Building" button click).
   *  Used as the authoritative start of the Assembly stage when no explicit
   *  assembly_started exists in stage_timestamps. */
  orderStart?: string | null;
  /** When the order finished (completed_at / completed_date). Used as the
   *  upper anchor for synthesising trailing stages. Leave undefined for
   *  in-progress orders so we don't invent future "completed" stages. */
  orderEnd?: string | null;
  /** Set of YYYY-MM-DD strings counted as working days for unmanaged techs.
   *  Pass undefined for managed techs (defaults to all weekdays). */
  activeDays?: Set<string>;
  /** Merged pause intervals; paused time is left out of each stage's
   *  duration. Stage start and end times are unchanged. */
  pauses?: ReadonlyArray<Interval>;
}

/**
 * Build a complete stage timeline from a flat stage_timestamps object.
 *
 * Goals:
 *  - Always emit a row for every section (no skipping).
 *  - Use explicit `*_started` / `*_completed` from `src` when present.
 *  - For Assembly, fall back to `orderStart` (the "Start Building" click)
 *    when no explicit assembly_started exists.
 *  - For sections with no data, synthesise started/completed by
 *    distributing the available *business* time between known anchors
 *    across the missing sections in proportion to SECTION_WEIGHTS, walked
 *    forward via addBusinessMs so synthesised boundaries always land
 *    inside working hours.
 *  - When stages are batch-completed (0m durations from a single checklist
 *    save), redistribute the total span across all completed stages.
 *  - Snap each row's started/completed to the nearest whole minute so the
 *    displayed times and the duration math are derived from identical
 *    minute-precision values.
 */
export function buildStageTimeline(
  src: Record<string, any> | null | undefined,
  options: BuildStageTimelineOptions = {},
): StageRow[] {
  const ts = src ?? {};
  const { orderStart = null, orderEnd = null, activeDays, pauses } = options;
  /** Business time worked between two instants: lunch left in, pauses out. */
  const workedMs = (s: Date, e: Date) =>
    businessMs(s, e, activeDays, true) - pausedBusinessMs(s, e, pauses, activeDays);

  // ── 1. Collect explicit per-section data ───────────────────
  type Known = { started: string | null; completed: string | null };
  const known: Record<string, Known> = {};
  for (const [key] of STAGE_TIMELINE_SECTIONS) {
    known[key] = {
      started: (ts[`${key}_started`] as string | undefined) || null,
      completed: (ts[`${key}_completed`] as string | undefined) || null,
    };
  }
  // Assembly: tech pressed Start Building → orderStart is the authoritative
  // start of assembly when no checklist tick has been recorded yet.
  if (!known.assembly.started && orderStart) {
    known.assembly.started = orderStart;
  }

  // Bail out if there is literally nothing to show.
  const anyKnown = Object.values(known).some(k => k.started || k.completed);
  if (!anyKnown && !orderStart) return [];

  // ── 2. Set up boundary array (N+1 boundaries for N sections) ──
  // boundaries[i] is the start of section i (and the end of section i-1).
  const N = STAGE_TIMELINE_SECTIONS.length;
  const boundaries: (string | null)[] = new Array(N + 1).fill(null);
  // Track whether each boundary came from real recorded data (true) or was
  // synthesised by weight distribution (false). Used to avoid marking a
  // stage as "estimated" when its boundary is anchored by an adjacent
  // section's explicit timestamp (e.g. assembly end == software start).
  const boundaryIsExplicit: boolean[] = new Array(N + 1).fill(false);
  const setBoundaryMax = (idx: number, ts: string, explicit = false) => {
    const cur = boundaries[idx];
    if (!cur || new Date(ts) > new Date(cur)) {
      boundaries[idx] = ts;
      boundaryIsExplicit[idx] = explicit;
    } else if (explicit) {
      // Same or earlier value — but now we know it's an explicit anchor.
      boundaryIsExplicit[idx] = true;
    }
  };

  // orderStart is the "Start Building" button click — a real recorded event.
  if (orderStart) setBoundaryMax(0, orderStart, true);
  // Only anchor the upper bound when the order is finished — otherwise we
  // would falsely "complete" stages that haven't actually happened yet.
  if (orderEnd) setBoundaryMax(N, orderEnd, true);

  for (let i = 0; i < N; i++) {
    const [key] = STAGE_TIMELINE_SECTIONS[i];
    if (known[key].started) setBoundaryMax(i, known[key].started!, true);
    if (known[key].completed) setBoundaryMax(i + 1, known[key].completed!, true);
  }

  // ── 3. Fill null boundary gaps by weight, in business time ──
  let i = 1;
  while (i < N) {
    if (boundaries[i] !== null) { i++; continue; }
    let j = i;
    while (j <= N && boundaries[j] === null) j++;
    if (j > N || boundaries[i - 1] === null || boundaries[j] === null) {
      // No anchor on one side → can't synthesise this run.
      i = j;
      continue;
    }
    const startBoundary = new Date(boundaries[i - 1]!);
    const endBoundary = new Date(boundaries[j]!);
    const availableMs = businessMs(startBoundary, endBoundary, activeDays);

    const affected: number[] = [];
    for (let s = i - 1; s <= j - 1; s++) affected.push(s);
    const totalWeight = affected.reduce(
      (sum, idx) => sum + (SECTION_WEIGHTS[STAGE_TIMELINE_SECTIONS[idx][0]] || 1),
      0,
    );

    let cursor = startBoundary;
    for (let s = 0; s < affected.length - 1; s++) {
      const sIdx = affected[s];
      const w = SECTION_WEIGHTS[STAGE_TIMELINE_SECTIONS[sIdx][0]] || 1;
      const sliceMs = totalWeight > 0 ? Math.round(availableMs * (w / totalWeight)) : 0;
      cursor = addBusinessMs(cursor, sliceMs, activeDays);
      // Snap to whole minute (see snapIsoToMinute) — applied here so cumulative
      // sub-minute drift can't accumulate across sections.
      const secs = cursor.getSeconds() + cursor.getMilliseconds() / 1000;
      if (secs >= 30) cursor.setMinutes(cursor.getMinutes() + 1);
      cursor.setSeconds(0, 0);
      boundaries[sIdx + 1] = cursor.toISOString();
    }
    i = j;
  }

  // ── 4. Build rows from boundaries ──────────────────────────
  const rows: StageRow[] = [];
  for (let i = 0; i < N; i++) {
    const [key, label] = STAGE_TIMELINE_SECTIONS[i];
    const startedB = boundaries[i];
    const completedB = boundaries[i + 1];
    if (!startedB) continue; // section hasn't started — skip when no orderStart

    const explicitStarted = !!known[key].started;
    const explicitCompleted = !!known[key].completed;
    const inProgress = !explicitCompleted && !orderEnd;

    // Snap displayed values to whole minutes so duration math (which
    // operates on the raw timestamp) matches what's rendered in the
    // Started/Completed columns (which drop seconds).
    const startedSnap = snapIsoToMinute(startedB);
    const completedSnap = completedB ? snapIsoToMinute(completedB) : null;

    const s = new Date(startedSnap);
    const e = completedSnap ? new Date(completedSnap) : new Date();
    const stagePausedMs = pausedBusinessMs(s, e, pauses, activeDays);

    rows.push({
      stage: label + (inProgress && !completedSnap ? ' (in progress)' : ''),
      key,
      started: startedSnap,
      completed: completedSnap,
      duration: stagePausedMs > 0 ? formatDurationMs(workedMs(s, e)) : formatBusinessDuration(s, e, activeDays, true),
      ...(stagePausedMs > 0 && { pausedMs: stagePausedMs }),
      // Mark as estimated when either endpoint had to be synthesised.
      // Assembly's orderStart fallback is treated as authoritative — the
      // "Start Building" click is a real, recorded event.
      // A boundary is also explicit when it is anchored by the adjacent
      // section's real timestamp (e.g. assembly end == software_started),
      // so we check boundaryIsExplicit rather than only explicitCompleted.
      estimated: (!explicitStarted && key !== 'assembly') || (!boundaryIsExplicit[i + 1] && !!completedSnap && !inProgress),
    });
  }

  // ── 5. Batch-click detection and estimation ───────────────
  // When a tech completes multiple stages simultaneously, each stage's
  // duration is unreliable. Detection signal: 2+ consecutive stages whose
  // raw business duration falls below their stage minimum — a single short
  // stage could be legitimately quick, but a run of them is always a batch.
  //
  // Additionally, if the stage immediately before the batch run completed at
  // the same timestamp the batch started, it was part of the same click.
  //
  // Estimation: distribute remaining order time (total minus real stage
  // durations) proportionally by weight among all batch-pool stages.
  //   estimate = remaining × (weight / batch_weight_sum)
  //   then clamp to max(estimate, stage minimum)
  {
    // Raw business duration per row (recomputed from timestamps, not the
    // formatted string, so lunch-deduction quirks don't affect detection).
    const rawMs = new Map<string, number>();
    for (const row of rows) {
      if (row.started && row.completed) {
        rawMs.set(row.key, workedMs(new Date(row.started), new Date(row.completed)));
      }
    }

    const isSuspect = (row: StageRow) => {
      const min = STAGE_MIN_DURATION_MS[row.key];
      if (!min) return false;
      return (rawMs.get(row.key) ?? 0) < min;
    };

    // Collect all stages that belong to a batch (run of 2+ suspects).
    const inBatchPool = new Set<string>();
    for (let i = 0; i < rows.length; i++) {
      if (!isSuspect(rows[i])) continue;
      let j = i;
      while (j < rows.length && isSuspect(rows[j])) j++;
      if (j - i >= 2) {
        for (let k = i; k < j; k++) inBatchPool.add(rows[k].key);
        // Pull in the preceding stage if it completed at the batch start moment.
        if (i > 0 && rows[i - 1].completed === rows[i].started) {
          inBatchPool.add(rows[i - 1].key);
        }
      }
      i = j - 1;
    }

    if (inBatchPool.size > 0) {
      const toEstimate = rows.filter(r => inBatchPool.has(r.key) && r.started && r.completed);

      const firstStarted = rows[0].started;
      const lastCompleted = [...rows].reverse().find(r => r.completed)?.completed;
      const orderTotal = firstStarted && lastCompleted
        ? workedMs(new Date(firstStarted), new Date(lastCompleted))
        : 0;

      // Subtract real (non-batch) stage durations to isolate unaccounted time.
      const knownMs = rows
        .filter(r => !inBatchPool.has(r.key) && r.started && r.completed)
        .reduce(
          (sum, r) => sum + workedMs(new Date(r.started!), new Date(r.completed!)),
          0,
        );
      const remaining = Math.max(0, orderTotal - knownMs);

      // Iterative allocation: stages whose proportional share falls below
      // their minimum are clamped first and their allocation is reserved from
      // the pool. The remainder is then re-distributed among unclamped stages.
      // Repeat until stable so that cascading clamps are handled correctly.
      // This ensures sum(allocated) == remaining exactly.
      let pool = remaining;
      const unresolved = new Set(toEstimate.map(r => r.key));
      const allocated  = new Map<string, number>();

      let changed = true;
      while (changed) {
        changed = false;
        const freeWeight = toEstimate
          .filter(r => unresolved.has(r.key))
          .reduce((s, r) => s + (SECTION_WEIGHTS[r.key] || 1), 0);
        for (const row of toEstimate) {
          if (!unresolved.has(row.key)) continue;
          const weight = SECTION_WEIGHTS[row.key] || 1;
          const minMs  = STAGE_MIN_DURATION_MS[row.key] ?? 0;
          const proportional = freeWeight > 0 ? Math.round(pool * (weight / freeWeight)) : 0;
          if (proportional < minMs) {
            allocated.set(row.key, minMs);
            pool = Math.max(0, pool - minMs);
            unresolved.delete(row.key);
            changed = true;
          }
        }
      }
      // Distribute remaining pool proportionally among unclamped stages.
      {
        const freeWeight = toEstimate
          .filter(r => unresolved.has(r.key))
          .reduce((s, r) => s + (SECTION_WEIGHTS[r.key] || 1), 0);
        for (const row of toEstimate) {
          if (!unresolved.has(row.key)) continue;
          const weight = SECTION_WEIGHTS[row.key] || 1;
          allocated.set(row.key, freeWeight > 0 ? Math.round(pool * (weight / freeWeight)) : 0);
        }
      }

      for (const row of toEstimate) {
        const stageMs = allocated.get(row.key) ?? 0;
        const h = Math.floor(stageMs / 3_600_000);
        const m = Math.round((stageMs % 3_600_000) / 60_000);
        row.duration  = h > 0 ? `${h}h ${m}m` : `${m}m`;
        row.estimated = true;
      }
    }
  }

  // ── 6. Minimum-duration validation ───────────────────
  // Estimated durations that fall below their stage minimum indicate the
  // estimation logic couldn't produce a sensible value (e.g. Finishing
  // restored to 1m from a lunch-zeroed boundary, below the 5m requirement).
  // Clamp those to the minimum so the displayed estimate is always credible.
  // Non-estimated rows (real timestamps) are never touched.
  for (const row of rows) {
    if (!row.estimated || !row.completed) continue;
    const minMs = STAGE_MIN_DURATION_MS[row.key];
    if (!minMs) continue;
    // Parse current duration string back to ms.
    const hMatch = row.duration.match(/(\d+)h/);
    const mMatch = row.duration.match(/(\d+)m/);
    const currentMs =
      ((hMatch ? parseInt(hMatch[1]) : 0) * 60 +
       (mMatch ? parseInt(mMatch[1]) : 0)) * 60_000;
    if (currentMs < minMs) {
      const h = Math.floor(minMs / 3_600_000);
      const m = Math.round((minMs % 3_600_000) / 60_000);
      row.duration = h > 0 ? `${h}h ${m}m` : `${m}m`;
    }
  }

  return rows;
}
